import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, FormControl, InputLabel, Link, MenuItem,
  Paper, Select, TextField, Typography,
} from '@mui/material';
import { AutoAwesome as ClaudeIcon } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import ConfirmDialog from './ConfirmDialog';

const MODEL_LABELS = {
  'claude-sonnet-5-5': 'Claude Sonnet 5.5 (recommended)',
  'claude-haiku-4-5-20251001': 'Claude Haiku 4.5 (faster, cheaper)',
  'claude-opus-5-5': 'Claude Opus 5.5 (most capable)',
};

/**
 * Profile → Claude: the user's own Anthropic API key, which enables Operations View → Obs →
 * Plan Observation. The key is verified with Anthropic, stored encrypted, and never shown again —
 * only its last four characters.
 */
const ClaudeKeySection = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('claude-sonnet-5-5');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getMyAiCredentials();
      setInfo(res.data);
      setModel(res.data.model);
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Could not load your Claude settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await apiService.saveMyAiCredentials({ ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}), model });
      setInfo(res.data);
      setApiKey('');
      setSuccess(apiKey.trim() ? 'Key verified and saved. Plan Observation is now available in the Obs menu.' : 'Model updated.');
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Could not save the key');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setConfirmRemove(false);
    try {
      const res = await apiService.deleteMyAiCredentials();
      setInfo(res.data);
      setSuccess('Key removed. Plan Observation is turned off for your account.');
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Could not remove the key');
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }} data-testid="claude-key-section">
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ClaudeIcon />
        Claude (AI observation planning)
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add your own Anthropic API key to use <strong>Plan Observation</strong> in the Operations View Obs menu.
        The assistant reads your observatories, schedules and past observations, and proposes observations
        you confirm before anything is submitted. Usage is billed to your Anthropic account. Create a key at{' '}
        <Link href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer">console.anthropic.com</Link>.
      </Typography>

      {loading ? <CircularProgress size={20} /> : (
        <>
          {info?.planning_enabled === false && (
            <Alert severity="warning" sx={{ mb: 2 }}>An administrator has turned the planning assistant off on this server.</Alert>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {info?.configured ? (
              <>
                <Chip size="small" color="success" label={`Key on file …${info.key_last4}`} />
                {info.verified_at && (
                  <Typography variant="caption" color="text.secondary">
                    verified {new Date(info.verified_at).toLocaleDateString()}
                  </Typography>
                )}
              </>
            ) : <Chip size="small" label="No key" />}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              size="small"
              type="password"
              autoComplete="off"
              label={info?.configured ? 'Replace key' : 'Anthropic API key'}
              placeholder="sk-ant-…"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              sx={{ minWidth: 320, flex: 1 }}
              inputProps={{ 'data-testid': 'claude-key-input' }}
            />
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Model</InputLabel>
              <Select label="Model" value={model} onChange={(e) => setModel(e.target.value)}>
                {(info?.models || Object.keys(MODEL_LABELS)).map((m) => (
                  <MenuItem key={m} value={m}>{MODEL_LABELS[m] || m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              onClick={save}
              disabled={saving || (!apiKey.trim() && (!info?.configured || model === info?.model))}
            >
              {saving ? 'Verifying…' : 'Save'}
            </Button>
            {info?.configured && (
              <Button color="error" onClick={() => setConfirmRemove(true)}>Remove key</Button>
            )}
          </Box>
        </>
      )}

      <ConfirmDialog
        open={confirmRemove}
        title="Remove Claude key?"
        message="Plan Observation will be turned off for your account. Your saved planning preferences are kept."
        confirmLabel="Remove"
        confirmColor="error"
        onConfirm={remove}
        onClose={() => setConfirmRemove(false)}
      />
    </Paper>
  );
};

export default ClaudeKeySection;
